import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useHashFlag from '../../../hooks/useHashFlag';
import HASH_FLAGS from '../../../routes/hashFlags.const';
import {
  RACHEL_PAGE_CONTEXT_LABEL_MAX_LENGTH,
  RachelPageContext,
} from '../rachelPageContext.types';
import { getRachelFocusedEntity, getRachelPageEntity } from '../rachelPageContext.utils';

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const getFallbackLabel = (pathname: string): string => {
  const segment = pathname.split('/').filter(Boolean).at(-1);
  if (!segment) {
    return 'início';
  }

  return decodeSegment(segment).replace(/[-_]/g, ' ');
};

export const useRachelCurrentPageContext = (): RachelPageContext => {
  const { pathname } = useLocation();
  const { value: focusedProductColorId } = useHashFlag(HASH_FLAGS.productDetails);
  const [titleSnapshot, setTitleSnapshot] = useState(() => ({
    pathname,
    title: document.title.trim(),
  }));

  useEffect(() => {
    const syncTitle = () => setTitleSnapshot({ pathname, title: document.title.trim() });
    const animationFrameId = requestAnimationFrame(syncTitle);

    const titleElement = document.querySelector('title');
    if (!titleElement) {
      return () => cancelAnimationFrame(animationFrameId);
    }

    const observer = new MutationObserver(syncTitle);
    observer.observe(titleElement, { childList: true });
    return () => {
      cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [pathname]);

  const currentDocumentTitle =
    titleSnapshot.pathname === pathname ? titleSnapshot.title : undefined;

  return {
    pathname,
    ...getRachelPageEntity(pathname),
    ...getRachelFocusedEntity(focusedProductColorId),
    label: (currentDocumentTitle || getFallbackLabel(pathname)).slice(
      0,
      RACHEL_PAGE_CONTEXT_LABEL_MAX_LENGTH,
    ),
  };
};
